/**
 * Program Mapping Implementation
 * 
 * This component implements a portal user to program user mapping system with the following features:
 * 
 * 1. First-time login mapping: When user logs into a program (NCD/Sickle/Nikshay etc.) for the first time,
 *    it stores a mapping between portal_user_id + program_id + program_user_identifier
 * 
 * 2. Existing mapping conflict: If user tries to login with different credentials for same program,
 *    shows a popup asking if they want to replace the existing mapping
 * 
 * 3. Auto-login on portal login: When user logs into main portal, automatically logs them into
 *    all mapped & active programs using stored credentials/tokens
 * 
 * 4. Program-level logout: Allows unmapping specific programs from settings page without
 *    affecting other program mappings
 * 
 * 5. Main portal logout: Complete logout that clears all mappings and redirects to login
 */

import { useState, useEffect } from "react";
import "./Setting.css";
import { useLanguage } from '../../../utils/languageContext';
import { useCommon } from "../../../context/CommonContext";
import { Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios"; // Import axios
import { toast } from "react-toastify";
import Loader from "../../../components/Loader";
import { uwinAPI } from "../../../services/uwin.api";
import { programLogos } from "../../../AppConstant/programLogos";
import Footer from "../../../components/Footer";
import { encryptData, getUserRole } from "../../../helpers/common-helper";
import { rchAPI } from "../../../services/rch.api";
import { eopdAPI } from "../../../services/e-opd.api";

export const Setting = () => {
	const { t } = useLanguage();
	const [showModal, setShowModal] = useState(false);
	const [showMappingModal, setShowMappingModal] = useState(false);
	const [currentProgramIndex, setCurrentProgramIndex] = useState(0);
	const [mappingPrograms, setMappingPrograms] = useState([]);
	const [showLoginForm, setShowLoginForm] = useState(false);
	const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
	const [logoutProgramName, setLogoutProgramName] = useState('');
	const [showReplaceModal, setShowReplaceModal] = useState(false);
	const [replaceMappingData, setReplaceMappingData] = useState(null);
	const [modalText, setModalText] = useState("");
	const [imgSrc, setImgSrc] = useState("");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [errorMessage, setErrorMessage] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [program, setProgram] = useState("SASHAKT");
	const [user, setUser] = useState(null);
	const [activePrograms, setActivePrograms] = useState(null);
	const [isPageLoading, setIsPageLoading] = useState(false);
	const [captcha, setCaptcha] = useState("1234");
	const [refreshTrigger, setRefreshTrigger] = useState(0);

	const navigate = useNavigate();

	//Sickle cell API integration
	const [mobileNumber, setMobileNumber] = useState("");
	const [sickleOtp, setSickleOtp] = useState(new Array(6).fill(""));
	const [SickleOtpText, setSickleOtpText] = useState("");
	const [ShowSendOtp, setShowSendOtp] = useState(false);
	const [ShowUwinSendOtp, setShowUwinSendOtp] = useState(false);
	const [uwinOtp, setUwinOtp] = useState(new Array(6).fill(""));
	const [UwinOtpText, setUwinOtpText] = useState("");
	const [uwinTxnId, setUwinTxnId] = useState("");
	const [resendTimer, setResendTimer] = useState(0); // Timer starts from 30 seconds

	const { ncduser, setNcdUser } = useCommon();

	// ================================Backgound Scroll Stop ================================
	useEffect(() => {
		if (showModal || showMappingModal || showReplaceModal || showLogoutConfirm) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "auto";
		}

		return () => {
			document.body.style.overflow = "auto";
		};
	}, [showModal, showMappingModal, showReplaceModal, showLogoutConfirm]);
	// =====================================End ===============================================
	// Countdown logic using useEffect
	useEffect(() => {
		if (resendTimer > 0) {
			const timer = setTimeout(() => setResendTimer((prev) => prev - 1), 1000);
			return () => clearTimeout(timer); // Cleanup timer on unmount
		}
	}, [resendTimer]);

	useEffect(() => {

		checkLoggedIn();

		const initialize = async () => {
			const userData = localStorage.getItem("loggedinUserdetails") || localStorage.getItem("NIKSHAY_loggedinUserdetails") || localStorage.getItem("RBSK_loggedinUserdetails") || localStorage.getItem("JANANI_loggedinUserdetails");
			let items = {};
			for (let i = 0; i < localStorage.length; i++) {
				let key = localStorage.key(i);
				items[key] = localStorage.getItem(key);
			}

			if (userData) {
				try {
					const parsedUser = JSON.parse(userData);

					setUser(parsedUser);

					// Set user_map_id if not already set
					if (!localStorage.getItem("user_map_id")) {
						const userMapId = parsedUser.username || parsedUser.InchargeMobile || parsedUser.ncdusername;
						if (userMapId) {
							localStorage.setItem("user_map_id", userMapId);
						}
					}

					// Get active programs
					await getActivePrograms();

					// Auto-login mapped programs
					await autoLoginPrograms();

					// Check if first time user and show mapping modal (but not for PMNDP users)
					const hasSeenMapping = localStorage.getItem('hasSeenProgramMapping');
					const isPMNDPUser = localStorage.getItem('PMNDP_token');
					if (!hasSeenMapping && !isPMNDPUser) {
						setShowMappingModal(true);
					}
				} catch (error) {
					console.error("Error during initialization:", error);
				}
			}
		};

		initialize(); // Call the async function

	}, []);

	const checkLoggedIn = () => {
		// Only check for tokens that are still present in localStorage
		const tokens = ["NCD_token", "sickle_token", "PMNDP_token", "NIKSHAY_valid_user", "uwin_token", "JANANI_token", "RBSK_token", "e-OPD_token"];
		// If none of the tokens exist, redirect to login
		const anyToken = tokens.some((token) => !!localStorage.getItem(token));

		if (!anyToken) {
			localStorage.setItem('hasSeenProgramMapping', '');
			navigate("/");
		}
	};

	const getActivePrograms = async () => {
		try {
			setIsPageLoading(true);

			// Always get all available programs for now
			const response = await axios.get(`/nodeapi/aggregator/active-programs`);
			if (response.data.success || response.data.programs) {

				// / const programs = response.data.programs || response.data;

				const programs = (response.data?.programs || []).filter(
					(program) => program.onlymobile != 1
				);

				// const programs = response.data.programs.filter(
				// 	item => item.program_name === "Nikshay"
				// );

				setActivePrograms(programs);

				// Set programs for mapping (exclude already logged in ones)

				// const availablePrograms = (Array.isArray(programs) ? programs : [])?.filter(prog => {
				//   if (prog.program_name === "NCD" && !localStorage.getItem("NCD_token")) return true;
				//   if (prog.program_name === "SCD" && !localStorage.getItem("sickle_token")) return true;
				//   if (prog.program_name === "Nikshay" && !localStorage.getItem("NIKSHAY_valid_user")) return true;
				//   if (prog.program_name === "UWIN" && !localStorage.getItem("uwin_token")) return true;
				//   return false;
				// }) || [];

				const availablePrograms = (Array.isArray(programs) ? programs : []).filter(prog => prog.program_name?.toUpperCase() !== "PMNDP");

				setMappingPrograms(availablePrograms);
			}

			setIsPageLoading(false);
		} catch (error) {
			console.error("Error in getting program list:", error);
			setIsPageLoading(false);
			// Set empty arrays as fallback
			setActivePrograms([]);
			setMappingPrograms([]);
		}
	};


	const mapUsername = async (programName, userId) => {
		try {
			setIsPageLoading(true);

			const mainUserId = localStorage.getItem("portal_main_user_id"); // must match DB's user_id

			const response = await axios.post("/nodeapi/program-mapping/map-settings", {
				program_id: programName,
				main_user_id: mainUserId,
				new_user_ids: [userId]   // directly pass the array
			});

			if (response.data.success) {

				const conflictItem = response.data.data?.find(item => item.status === "conflict");

				if (conflictItem) {

					// Show replace modal
					setReplaceMappingData({
						program: programName,
						existingUser: conflictItem.new_user_id,
						currentUser: userId,
						mainUserId: mainUserId
					});

					setShowReplaceModal(true);

					toast.error(`This account is already mapped with another account.`, {
						className: "custom-toast-error"
					});

					setIsPageLoading(false);

					return false;

				} else {
					await getActivePrograms();
					setIsPageLoading(false);
					return true;
				}
			}

			setIsPageLoading(false);
			return false;
		} catch (error) {
			console.error("Error in mapping username:", error);
			setIsPageLoading(false);
			throw error;
		}
	};

	const replaceMapping = async (programName, newCredentials) => {
		try {
			setIsLoading(true);

			const response = await axios.put("/nodeapi/program-mapping/replace", {
				program_name: programName,
				new_credentials: newCredentials
			});

			if (response.data.success) {
				toast.success(`${programName} account mapping has been updated successfully!`);
				await getActivePrograms();
				return true;
			}
			return false;
		} catch (error) {
			console.error("Error replacing mapping:", error);
			toast.error("Failed to update mapping");
			return false;
		} finally {
			setIsLoading(false);
		}
	};

	const unmapProgram = async (programName, programId, userId) => {
		try {
			setIsLoading(true);
			const response = await axios.post("/nodeapi/program-mapping/logout-program", {
				user_id: userId,
				program_id: programId
			});

			if (!response.data.success) return false;

			const programStorageMap = {
				NCD: ["NCD_token", "NCD_refresh_token", "ncd_username", "NCD_loggedinUserdetails", "NCD_PERMISSIONS"],
				SATH: ["sickle_token", "sickle_refresh_token", "sickle_username", "SICKLE_loggedinUserdetails"],
				"Sickle Cell": ["sickle_token", "sickle_refresh_token", "sickle_username", "SICKLE_loggedinUserdetails"],
				Nikshay: ["NIKSHAY_valid_user", "NIKSHAY_loggedinUserdetails", "NIKSHAY_Permissions"],
				"U-WIN": ["uwin_token", "uwin_refresh_token", "uwin_username", "uwin_loggedinUserdetails", "UWIN_Permissions"],
				PMNDP: ["PMNDP_token", "PMNDP_loggedinUserdetails"],
				JANANI: ["JANANI_token", "JANANI_refreshToken", "JANANI_loggedinUserdetails", "janani_username", "loggedinUserdetails", "janani_username", "portal_main_user_id", "user_map_id"],
				RBSK: ["RBSK_token", "RBSK_loggedinUserdetails", "rbsk_username", "loggedinUserdetails", "portal_main_user_id", "user_map_id"],
				"e-OPD": ["e-OPD_token", "e-OPD_refresh_token", "e-OPD_username", "e-OPD_loggedinUserdetails", "loggedinUserdetails", "portal_main_user_id", "user_map_id"],
			};

			(programStorageMap[programName] || []).forEach(key =>
				localStorage.removeItem(key)
			);

			toast.success(`${programName} account has been unmapped successfully!`);
			await getActivePrograms();
			return true;

		} catch (error) {
			console.error("Error unmapping program:", error);
			toast.error("Failed to unmap program");
			return false;
		} finally {
			setIsLoading(false);
		}
	};





	const getProgramPermissions = async (programId, role) => {
		try {
			setIsPageLoading(false);

			const response = await axios.get(
				`/nodeapi/aggregator/program-permissions`,
				{
					params: { programId, role }
				}
			);

			return response.data?.permissions || [];
		} catch (error) {
			console.error("Error in getting program permissions:", error);
			return [];
		} finally {
			setIsPageLoading(false);
		}
	};

	const autoLoginPrograms = async () => {
		try {
			const userMapId = localStorage.getItem("user_map_id");
			if (!userMapId) return;
			const response = await axios.get(`/nodeapi/program-mapping/auto-login/${userMapId}`);

			if (response.data.success && response.data.data) {
				const programMappings = response.data.data;
				// Process each program mapping
				programMappings.forEach(mapping => {
					if (mapping.is_active && mapping.status === 1 && mapping.ProgramMapping) {
						const programName = mapping.ProgramMapping.program_name;

						// full programDetails object from backend
						const programDetails = mapping.programDetails;

						// 🔹 Parse response_login_api JSON string (if present)
						let loginResponse = null;

						if (programDetails && programDetails.response_login_api) {
							try {
								loginResponse = JSON.parse(programDetails.response_login_api); // now it's an object
							} catch (e) {
								console.error("Error parsing response_login_api JSON:", e);
							}
						}

						if (!programDetails) return;
						if (!loginResponse && programName !== "Nikshay") return;

						if (programName === "NCD") {
							const existingToken = localStorage.getItem("NCD_token");
							const existingRefreshToken = localStorage.getItem("NCD_refresh_token");

							if (!existingToken && !existingRefreshToken) {
								localStorage.setItem("NCD_token", loginResponse?.access_token || "");
								localStorage.setItem("NCD_refresh_token", loginResponse?.refresh_token || "");
							}

							const userdetail = {
								firstname: loginResponse.username,
								rolename: getUserRole(loginResponse.username),
								facilityName: loginResponse.facilityName,
								facilitydetail: `${loginResponse.facilityType}, ${loginResponse.facilityName}`,
								username: loginResponse.username,
								ncdusername: loginResponse.username,
								loginuser: loginResponse.user,
								stateCode: loginResponse.stateCode,
								facility: loginResponse.facility,
								facilityTypeId: loginResponse.facilityTypeId,
								facilityType: loginResponse.facilityType
							};

							if (!localStorage.getItem("NCD_loggedinUserdetails")) {
								localStorage.setItem("NCD_loggedinUserdetails", JSON.stringify(userdetail));
							}
							if (!localStorage.getItem("loggedinUserdetails")) {
								localStorage.setItem("loggedinUserdetails", JSON.stringify(userdetail));
							}

							getProgramPermissions(1, getUserRole(loginResponse.username)).then((ncdPermissions = []) => {
								if (!localStorage.getItem("NCD_PERMISSIONS")) {
									localStorage.setItem("NCD_PERMISSIONS", JSON.stringify(ncdPermissions));
								}
							});

						} else if (programName === "SCD" || programName === "Sickle Cell") {
							if (!localStorage.getItem("sickle_token")) {
								localStorage.setItem("SATH_loggedinUserdetails", JSON.stringify(loginResponse));
							}
						} else if (programName === "Nikshay") {
							if (!localStorage.getItem("NIKSHAY_valid_user")) {
								getProgramPermissions(4, 'CHO').then((nikshayPermissions = []) => {
									localStorage.setItem("NIKSHAY_Permissions", JSON.stringify(nikshayPermissions));
								});
								localStorage.setItem("NIKSHAY_valid_user", true);
								localStorage.setItem("NIKSHAY_loggedinUserdetails", JSON.stringify(programDetails));
							}
						} else if (programName === "UWIN") {
							if (!localStorage.getItem("uwin_token")) {
								localStorage.setItem("uwin_token", programDetails.refresh_token);

								const uwin_userdetail = {
									firstname: loginResponse.display_name,
									rolename: loginResponse.role_name,
									facilitydetail: loginResponse.facility_name + ', ' + loginResponse.subdistrict_name + ', ' + loginResponse.district_name + ', ' + loginResponse.state_name,
									username: loginResponse.mobile_number,
									stateCode: loginResponse.state_code,
									districtCode: loginResponse.district_code,
									facilityTypeId: loginResponse.facility_id,
									InchargeMobile: email,
									user_id: loginResponse.user_id
								};

								localStorage.setItem("uwin_loggedinUserdetails", JSON.stringify(uwin_userdetail));

								getProgramPermissions(6, 'CHO').then((uwinPermissions = []) => {
									localStorage.setItem("UWIN_Permissions", JSON.stringify(uwinPermissions));
								});
							}
						} else if (programName === "PMNDP") {
							if (!localStorage.getItem("PMNDP_token")) {
								localStorage.setItem("PMNDP_loggedinUserdetails", JSON.stringify(loginResponse));
							}
						} else if (programName === "JANANI") {
							if (!localStorage.getItem("JANANI_token")) {
								const refreshToken = loginResponse?.auth_iam_status?.refresh_token;
								const accessToken = loginResponse?.auth_iam_status?.access_token;
								if (accessToken) {
									localStorage.setItem("JANANI_token", accessToken);
								}
								if (refreshToken) {
									localStorage.setItem("JANANI_refreshToken", refreshToken);
								}
								if (accessToken) {
									const userdetail = {
										firstname: loginResponse?.user_data[0]?.mob,
										rolename: loginResponse?.userdesignation_name,
										facilityName: loginResponse?.user_data[0]?.facility_name,
										username: loginResponse?.user_data[0]?.mob,
										loginuser: loginResponse?.user_data[0]?.mob,
										stateCode: loginResponse?.user_data[0]?.state_code,
										facilityId: loginResponse?.user_data[0]?.facility_id,
										facilityType: loginResponse?.user_data[0]?.facility_type_name,
										userId: loginResponse?.user_id,
										mobile: loginResponse?.user_data[0]?.mob,
									};

									localStorage.setItem("JANANI_loggedinUserdetails", JSON.stringify(userdetail));
									localStorage.setItem("janani_username", loginResponse?.user_data[0]?.mob);
								}
							}
						} else if (programName === "RBSK") {
							if (!localStorage.getItem("RBSK_token")) {
								const token = loginResponse?.data?.data?.token || loginResponse?.data?.token;
								if (token) {
									localStorage.setItem("RBSK_token", token);
									const user = loginResponse?.data?.data?.user || loginResponse?.data?.user;
									const userdetail = {
										firstname: user?.userName || user?.login_Name,
										rolename: user?.role_Name,
										facilityName: user?.state_Name || "",
										username: user?.login_Name || "",
										loginuser: user?.login_Name || "",
										stateCode: user?.state_ID,
										districtCode: user?.district_ID,
										userId: user?.id || user?.userID,
										mobile: user?.mobileNo,
									};
									localStorage.setItem("RBSK_loggedinUserdetails", JSON.stringify(userdetail));
									localStorage.setItem("rbsk_username", user?.login_Name || "");
								}
							}
						} else if (programName === "e-OPD") {
							if (!localStorage.getItem("e-OPD_token")) {
								const user = loginResponse?.response || loginResponse?.data || loginResponse;
								const token = user?.token || loginResponse?.data?.access_token || loginResponse?.access_token;
								if (token) {
									localStorage.setItem("e-OPD_token", token);
									localStorage.setItem("e-OPD_refresh_token", user?.refresh_token || loginResponse?.data?.refresh_token || "");
									const userdetail = {
										firstname: user?.name || user?.firstname || user?.role_name || user?.username || "",
										rolename: user?.role_name || "",
										facilityName: user?.facility_name || "",
										facilitydetail: user?.facility_name ? `${user.facility_name}${user.district ? ', ' + user.district : ''}` : "",
										username: user?.username || "",
										stateCode: user?.state_lgd || user?.state || "",
										districtCode: user?.district_lgd || user?.district || "",
										facilityTypeId: user?.facility_type || "",
										userId: user?.nin_id || user?.memberID || user?.username || "",
										mobile: user?.username || "",
									};
									localStorage.setItem("e-OPD_loggedinUserdetails", JSON.stringify(userdetail));
									localStorage.setItem("e-OPD_username", user?.username || "");
								}
							}
						}
					}
				});

				if (programMappings.length > 1) {
					toast.success("Mapped programs logged in successfully.");
				}
			}
		} catch (error) {
			console.error("Error in auto-login:", error);
		}
	};

	const handleMobileValidation = (e) => {
		if (!/^\d*$/.test(e.target.value)) return; // Allow only numeric values
		const inputValue = e.target.value;
		// Remove non-numeric characters
		const numericValue = inputValue.replace(/\D/g, "");
		setMobileNumber(numericValue);
	};

	const handleSickleOtpChange = (value, index) => {
		if (!/^\d*$/.test(value)) return; // Allow only numeric values

		const newOtp = [...sickleOtp];
		newOtp[index] = value;

		setSickleOtp(newOtp);

		// Convert OTP array to text and set it to OtpText
		const otpAsText = newOtp.join(""); // Joins all the digits into a single string
		setSickleOtpText(otpAsText);

		// Automatically move to the next input field
		if (value && index < 5) {
			document.getElementById(`otp-input-${index + 1}`).focus();
		}
	};

	const handleBackspace = (e, index) => {
		if (e.key === "Backspace" && !sickleOtp[index] && index > 0) {
			document.getElementById(`otp-input-${index - 1}`).focus();
		}
	};

	const handleSickleAuth = async () => {
		const captchaResponse = await sendCaptcha(mobileNumber);
		if (
			captchaResponse?.Status == 200 &&
			captchaResponse?.Message === "Success"
		) {
			if (captchaResponse?.value) {
				const verifyCaptchaResponse = await verifySickleCaptcha(
					mobileNumber,
					captchaResponse?.value
				);
				if (
					verifyCaptchaResponse.responseMetaData.responseStatusCode == 200 &&
					verifyCaptchaResponse.responseMetaData.message === "Success"
				) {
					setShowSendOtp(true);
					setResendTimer(60);
					toast.success("OTP send successfully");
				} else if (
					verifyCaptchaResponse.responseMetaData.responseStatusCode == 404 &&
					verifyCaptchaResponse.responseMetaData.message ===
					"Sorry! Captcha Not Matched"
				) {
					setShowSendOtp(true);
					toast.error(verifyCaptchaResponse.responseMetaData.message);
				}
			} else {
				toast.error("Something went wrong! please try again");
			}
		} else {
			toast.error(captchaResponse?.Message || "Sickle server is not responding. Please try again later.", {
				className: "custom-toast-error",
			});
		}
	};

	const verifySickleCaptcha = async (mobile, captch) => {
		try {
			const response = await axios.post(
				"/sickle2staging/api/facilitylogin/checkmobile",
				{
					mobileNumber: mobile,
					appversion: "2.0.1",
					captcha: captch,
				}
			);
			return response.data;
		} catch (error) {
			if (error.response.data?.error) {
				toast.error(error.response.data.error, {
					className: "custom-toast-error",
				});
			}
		}
	};

	const sendCaptcha = async (mobile) => {
		try {
			const response = await axios.post(
				"/sickle2staging/api/facilitylogin/sendCaptcha",
				{
					mobileNumber: mobile,
				}
			);
			return response.data;
		} catch (error) {
			if (error.response) {
				return { Status: error.response.status, Message: error.response.data?.error || "Server error" };
			} else if (error.request) {
				console.error(
					"Request was made, but no response received:",
					error.request
				);
				return { Status: 500, Message: "No response from server" };
			} else {
				console.error("Error in setting up the request:", error.message);
				return { Status: 500, Message: error.message };
			}
		}
	};

	const verifySickleOtp = async () => {
		try {
			const response = await axios.post(
				"/sickle2staging/api/facilitylogin2/checkotp",
				{
					mobileNumber: mobileNumber,
					otp: SickleOtpText,
				}
			);

			// if (String(user.stateCode).length === 3) {
			//   const actualStateCode = getActualStateCode(user.stateCode);
			//   if (response.data.result.state_id != actualStateCode) {
			//     toast.error("Mapping is allowed only if the state matches the state used in the previous program login.", {
			//       className: "custom-toast-error"
			//     });
			//     return false;
			//   }

			// } else {
			//   const actualStateCode = user.stateCode;
			//   if (response.data.result.state_id != actualStateCode) {
			//     toast.error("Mapping is allowed only if the state matches the state used in the previous program login.", {
			//       className: "custom-toast-error"
			//     });
			//     return false;
			//   }
			// }

			if (response.data.responseMetaData.responseStatusCode == "200") {
				localStorage.setItem("sickle_token", response.data.result.token);
				localStorage.setItem(
					"sickle_refresh_token",
					response.data.result.refresh_token
				);
				localStorage.setItem("sickle_username", mobileNumber);

				const resp = await mapUsername(2, mobileNumber);

				const userdetail = {
					firstname: response.data.result.fname,
					rolename: response.data.result.role_id == 5 ? "Health Facility User" : "",
					facilitydetail: "",
					username: response.data.result.fname,
					ncdusername: localStorage.getItem("ncd_username"),
					stateCode: response.data.result.state_id,
					facilityTypeId: response.data.result.facility_id,
					InchargeMobile: mobileNumber,
				};

				localStorage.setItem("loggedinUserdetails", JSON.stringify(userdetail));
				localStorage.setItem("SICKLE_loggedinUserdetails", JSON.stringify(userdetail));
				localStorage.setItem("user_map_id", mobileNumber);

				if (resp) {
					toast.success(`Sickle Cell account has been synced successfully!!`);
					setShowModal(false);
					setShowSendOtp(false);
				}

				// Continue mapping flow if in progress
				if (showMappingModal || currentProgramIndex < mappingPrograms.length) {
					handleSkipProgram();
				}
			} else {
				toast.error(
					response.data.responseMetaData.message || "Invalid Email or Password",
					{
						className: "custom-toast-error",
					}
				);
			}
		} catch (error) {
			if (error.response) {
				console.error("Server responded with a status:", error.response.status);
				console.error("Response data:", error.response.data);
			} else if (error.request) {
				console.error(
					"Request was made, but no response received:",
					error.request
				);
			} else {
				console.error("Error in setting up the request:", error.message);
			}
		}
	};


	const handleLabelClick = (e, modalType, srcValue) => {
		if (srcValue) {
			setImgSrc(srcValue); // Update the state with the src value
		}

		if (
			(modalType === "NCD" && ncd_token) ||
			(modalType == "SCD" && sickle_token) ||
			(modalType == "Nikshay" && nikshay_token) ||
			(modalType == "JANANI" && janani_token) ||
			(modalType == "U-WIN" && uwin_token) ||
			(modalType == "RBSK" && rbsk_token) ||
			(modalType === "e-OPD" && eopd_token) ||
			(modalType == "PMNDP" && pmndp_token)
		) {
			//setModalText(`${modalType} account has already been synced`);
			setModalText(
				`Are you sure you want to log out from the ${modalType} account?`
			);
		} else {
			setModalText(modalType); // Set the text to state
		}

		setProgram(modalType);

		// Call the openModal function
		setShowModal(true);
	};

	const closeModal = () => {
		setShowModal(false);
	};

	let ncd_token = localStorage.getItem("NCD_token");
	let sickle_token = localStorage.getItem("sickle_token");
	let nikshay_token = localStorage.getItem("NIKSHAY_valid_user");
	let janani_token = localStorage.getItem("JANANI_token");
	let uwin_token = localStorage.getItem("uwin_token");
	let pmndp_token = localStorage.getItem("PMNDP_token");
	let rbsk_token = localStorage.getItem("RBSK_token");
	let eopd_token = localStorage.getItem("e-OPD_token");
	const validateAndLoginLocal = () => {
		const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		const passwordPattern =
			/^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

		if (!emailPattern.test(email) || !passwordPattern.test(password)) {
			setErrorMessage("Please enter a valid Email and Password.");
		} else {
			setErrorMessage("");
			navigate("/dashboard");
		}
	};


	const getActualStateCode = (stateCode) => {
		const codeStr = String(stateCode);

		if (codeStr.length === 3 && codeStr.startsWith("1")) {
			return codeStr.slice(1); // remove prefix 1
		}

		return codeStr;
	};

	const loginNCD = async () => {
		try {

			setIsLoading(true);

			const encryptedEmail = encryptData(email);
			const encryptedPassword = encryptData(password);

			const response = await axios.post("/nodeapi/ncd/login", {
				email: encryptedEmail,
				password: encryptedPassword,
				captcha
			}, {
				headers: {
					"x-encrypted": "true"
				}
			});

			// const actualStateCode = user.stateCode;
			// if (getActualStateCode(response.data.data.stateCode) != actualStateCode) {
			//   toast.error("Mapping is allowed only if the state matches the state used in the previous program login.", {
			//     className: "custom-toast-error"
			//   });
			//   return false;
			// }

			if (response.status === 200) {

				const data = response.data.data;

				localStorage.setItem("NCD_token", data.access_token);
				localStorage.setItem("NCD_refresh_token", data.refresh_token);

				const userdetail = {
					firstname: data.username,
					rolename: data.username.includes('cho') ? 'CHO' : '',
					facilityName: data.facilityName,
					facilitydetail: `${data.facilityType}, ${data.facilityName}`,
					username: data.username,
					ncdusername: data.username,
					loginuser: data.user,
					stateCode: data.stateCode,
					facility: data.facility,
					facilityTypeId: data.facilityTypeId,
				};

				// ✅ 1. Update Context state
				setNcdUser(userdetail);

				// set localStorage for both NCD

				localStorage.setItem("loggedinUserdetails", JSON.stringify(userdetail));
				localStorage.setItem("NCD_loggedinUserdetails", JSON.stringify(userdetail));
				localStorage.setItem("NCD_PERMISSIONS", JSON.stringify(response.data.permission));
				localStorage.setItem("ncd_username", data.username);

				const resp = await mapUsername(1, data.username);

				if (resp) {
					toast.success(`NCD account has been synced successfully!!`);
					setShowModal(false);
				}

				// Continue mapping flow if in progress
				if (showMappingModal || currentProgramIndex < mappingPrograms.length) {
					handleSkipProgram();
				}
			} else {
				toast.error(response.data.message || "Invalid Email or Password", {
					className: "custom-toast-error",
				});
			}
		} catch (error) {
			toast.error(error.response?.data?.message, {
				className: "custom-toast-error",
			});
		} finally {
			setIsLoading(false);
		}
	};

	const loginNikshay = async () => {
		try {
			setIsLoading(true);

			const encryptedEmail = encryptData(email);
			const encryptedPassword = encryptData(password);

			const response = await axios.post("/nodeapi/nikshay/sso-login", {
				username: encryptedEmail,
				password: encryptedPassword
			}, {
				headers: {
					"Content-Type": "application/json",
					"x-encrypted": "true"
				}
			});

			// if (String(user.stateCode).length === 3) {
			//   const actualStateCode = getActualStateCode(user.stateCode);
			//   if (response.data.data.user.stateCode != actualStateCode) {
			//     toast.error("Mapping is allowed only if the state matches the state used in the previous program login.", {
			//       className: "custom-toast-error"
			//     });
			//     return false;
			//   }

			// } else {
			//   const actualStateCode = user.stateCode;
			//   if (response.data.data.user.stateCode != actualStateCode) {
			//     toast.error("Mapping is allowed only if the state matches the state used in the previous program login.", {
			//       className: "custom-toast-error"
			//     });
			//     return false;
			//   }
			// }

			if (response.data.success) {

				const responseData = response.data.data;

				localStorage.setItem("NIKSHAY_valid_user", responseData.user.active);

				const userdetail = {
					active: responseData.user.active,
					canDeletePatients: responseData.user.canDeletePatients,
					clientId: responseData.user.clientId,
					createdAt: responseData.user.createdAt,
					description: responseData.user.description,
					hierarchyId: responseData.user.hierarchyId,
					id: responseData.user.id,
					restrictedView: responseData.user.restrictedView,
					selectiveHierarchyMapping: responseData.user.selectiveHierarchyMapping,
					ssoId: responseData.user.ssoId,
					staffId: responseData.user.staffId,
					updatedAt: responseData.user.updatedAt,
					username: responseData.user.username,
					viewOnly: responseData.user.viewOnly,
					visible: responseData.user.visible
				};

				localStorage.setItem("NIKSHAY_loggedinUserdetails", JSON.stringify(userdetail));

				localStorage.setItem("NIKSHAY_Permissions", JSON.stringify(responseData?.permissions));

				const resp = await mapUsername(4, responseData.user.username);

				if (resp) {
					toast.success(`Nikshay account has been synced successfully!!`);
					setShowModal(false);
					setRefreshTrigger(prev => prev + 1); // Force re-render
				}

				// Continue mapping flow if in progress
				if (showMappingModal || currentProgramIndex < mappingPrograms.length) {
					handleSkipProgram();
				}
			} else {
				toast.error(response.data.msg || "Invalid username or Password", {
					className: "custom-toast-error"
				});
			}
		} catch (error) {
			toast.error("Username Or Password Incorrect", {
				className: "custom-toast-error"
			});
		} finally {
			setIsLoading(false);
		}
	};

	const loginJanani = async () => {
		try {
			setIsLoading(true);

			const response = await rchAPI.login({
				userName: email,
				password: password,
			});

			if (response?.success) {
				const data = response?.data?.data[0];

				if (data?.auth_status === "ALREADY_SESSION_EXISTS") {
					toast.error("Session already exists. Login not allowed.", {
						className: "custom-toast-error",
					});
					return;
				}

				if (data?.auth_status === "SUCCESS") {
					const refreshToken = data?.auth_iam_status?.refresh_token;
					const accessToken = data?.auth_iam_status?.access_token;
					localStorage.setItem("JANANI_refreshToken", refreshToken);
					localStorage.setItem("JANANI_token", accessToken);

					const userdetail = {
						firstname: data?.user_data[0]?.mob,
						rolename: data?.userdesignation_name,
						facilityName: data?.user_data[0]?.facility_name,
						username: data?.user_data[0]?.mob,
						loginuser: data?.user_data[0]?.mob,
						stateCode: data?.user_data[0]?.state_code,
						facilityId: data?.user_data[0]?.facility_id,
						facilityType: data?.user_data[0]?.facility_type_name,
						userId: data?.user_id,
						mobile: data?.user_data[0]?.mob,
					};

					localStorage.setItem("JANANI_loggedinUserdetails", JSON.stringify(userdetail));
					localStorage.setItem("janani_username", data?.user_data[0]?.mob);

					const resp = await mapUsername(5, data?.user_data[0]?.mob);

					if (resp) {
						toast.success(`JANANI account has been synced successfully!!`);
						setShowModal(false);
					}

					// Continue mapping flow if in progress
					if (showMappingModal || currentProgramIndex < mappingPrograms.length) {
						handleSkipProgram();
					}
				} else {
					toast.error(response.message || "Login failed", {
						className: "custom-toast-error",
					});
				}
			} else {
				toast.error(response.message || "Invalid credentials", {
					className: "custom-toast-error",
				});
			}
		} catch (error) {
			toast.error(error.response?.data?.message || "Login failed", {
				className: "custom-toast-error",
			});
		} finally {
			setIsLoading(false);
		}
	};

	const loginRBSK = async () => {
		try {
			setIsLoading(true);

			const response = await axios.post("/nodeapi/rbsk/rbsk-login", {
				userName: email,
				password: password,
				salt: "d3b07384d113edec",
			});

			if (response.data.success) {
				const responseData = response.data.data;

				if (responseData?.succecced) {
					const token = responseData?.data?.token;
					const user = responseData?.data?.user;
					const username = user?.login_Name || email;

					localStorage.setItem("RBSK_token", token);

					const userdetail = {
						firstname: user?.userName || user?.login_Name,
						rolename: user?.role_Name,
						facilityName: user?.state_Name || "",
						username: username,
						loginuser: username,
						stateCode: user?.state_ID,
						districtCode: user?.district_ID,
						userId: user?.id || user?.userID,
						mobile: user?.mobileNo,
					};

					localStorage.setItem("RBSK_loggedinUserdetails", JSON.stringify(userdetail));
					localStorage.setItem("loggedinUserdetails", JSON.stringify(userdetail));
					localStorage.setItem("rbsk_username", username);
					localStorage.setItem("user_map_id", username);
					localStorage.setItem("currentLoggedInProgramId", 11);

					const resp = await mapUsername(11, username);

					if (resp) {
						toast.success(`RBSK account has been synced successfully!!`);
						setShowModal(false);
					}

					// Continue mapping flow if in progress
					if (showMappingModal || currentProgramIndex < mappingPrograms.length) {
						handleSkipProgram();
					}
				} else {
					toast.error(responseData?.message || "Login failed", {
						className: "custom-toast-error",
					});
				}
			} else {
				toast.error(response.data.message || "Invalid credentials", {
					className: "custom-toast-error",
				});
			}
		} catch (error) {
			toast.error(error.response?.data?.message || "Login failed", {
				className: "custom-toast-error",
			});
		} finally {
			setIsLoading(false);
		}
	};

	const loginEOPD = async () => {
		try {
			setIsLoading(true);
			const response = await eopdAPI.login({
				username: email,
				password: password,
			});

			if (response.success) {
				const user = response.response || response.data || response;

				const token = user?.token || user?.access_token || user?.api_token;
				if (token) {
					localStorage.setItem("e-OPD_token", token);
					localStorage.setItem("e-OPD_refresh_token", user?.refresh_token || "");
					localStorage.setItem("e-OPD_username", user?.username || email);

					const userdetail = {
						firstname: user?.name || user?.firstname || user?.role_name || user?.username || email,
						rolename: user?.role_name || "",
						facilityName: user?.facility_name || "",
						facilitydetail: user?.facility_name ? `${user.facility_name}${user.district ? ', ' + user.district : ''}` : "",
						username: user?.username || email,
						stateCode: user?.state_lgd || user?.state || "",
						districtCode: user?.district_lgd || user?.district || "",
						facilityTypeId: user?.facility_type || "",
						userId: user?.nin_id || user?.memberID || user?.username || "",
						mobile: user?.username || "",
					};

					localStorage.setItem("e-OPD_loggedinUserdetails", JSON.stringify(userdetail));
					localStorage.setItem("loggedinUserdetails", JSON.stringify(userdetail));
					localStorage.setItem("user_map_id", user?.username || email);
					localStorage.setItem("currentLoggedInProgramId", 12);

					const resp = await mapUsername(12, user?.username || email);

					if (resp) {
						toast.success(`e-OPD account has been synced successfully!!`);
						setShowModal(false);
					}

					// Continue mapping flow if in progress
					if (showMappingModal || currentProgramIndex < mappingPrograms.length) {
						handleSkipProgram();
					}
				} else {
					toast.error("Invalid response format from e-OPD server", {
						className: "custom-toast-error",
					});
				}
			} else {
				toast.error(response.message || "Invalid credentials", {
					className: "custom-toast-error",
				});
			}
		} catch (error) {
			toast.error(error.response?.data?.message || "Login failed", {
				className: "custom-toast-error",
			});
		} finally {
			setIsLoading(false);
		}
	};

	const encryptText = async (plaintext) => {
		try {
			const resEncrypt = await axios.post("nodeapi/uwin/encryption", {
				plaintext: plaintext
			}, {
				headers: {
					"Content-Type": "application/json"
				}
			});
			return resEncrypt.data.data.encrypted;
		} catch (error) {
			console.error("Backend login error:", error);
		}
	};

	const loginUwin = async () => {
		try {
			setIsLoading(true);

			const response = await axios.post("nodeapi/uwin/user-login", {
				mobile_number: await encryptText(email),
				password: await encryptText(password)
			}, {
				headers: {
					"Content-Type": "application/json"
				}
			});
			if (response.data.success) {
				toast.success(response.data.message || "OTP Sent on your registered mobile number", {
					className: "custom-toast-success"
				});
				setUwinTxnId(response.data.data.txnId);
				setShowUwinSendOtp(true);
				setResendTimer(60);
			} else {
				toast.error(response.data.message || "Invalid Username or Password", {
					className: "custom-toast-error"
				});
			}
		} catch (error) {
			toast.error("Something went wrong !", {
				className: "custom-toast-error"
			});
		} finally {
			setIsLoading(false);
		}
	};


	const handleUwinVerifyOtp = async () => {
		try {
			setIsLoading(true);
			const response = await axios.post(
				'nodeapi/uwin/user-verify-otp',
				{
					txnId: uwinTxnId,
					otp: await encryptText(UwinOtpText),
					mobile_number: email
				}
			);

			if (response?.data?.data?.response?.accessToken) {

				const resp = await mapUsername(6, email);

				if (resp) {

					localStorage.setItem("uwin_token", response.data.data.response.accessToken);
					localStorage.setItem("uwin_refresh_token", response.data.data.response.refreshToken);

					const userResponse = await uwinAPI.getUserDetails();

					if (userResponse.success) {
						if (userResponse.data[0] || userResponse.data[0] > 0) {

							const userDetails = userResponse.data[0];

							const userdetail = {
								firstname: userDetails.display_name,
								rolename: userDetails.role_name,
								facilitydetail: userDetails.facility_name + ', ' + userDetails.subdistrict_name + ', ' + userDetails.district_name + ', ' + userDetails.state_name,
								username: userDetails.mobile_number,
								stateCode: userDetails.state_code,
								districtCode: userDetails.district_code,
								facilityTypeId: userDetails.facility_id,
								InchargeMobile: email,
								user_id: userDetails.user_id
							};

							localStorage.setItem("uwin_loggedinUserdetails", JSON.stringify(userdetail));
							localStorage.setItem("loggedinUserdetails", JSON.stringify(userdetail));
							localStorage.setItem("uwin_username", email);
							localStorage.setItem("UWIN_Permissions", JSON.stringify(response.data?.data?.permissions));

							toast.success(`U-WIN account has been synced successfully!!`);
							setShowModal(false);

							// Continue mapping flow if in progress
							if (showMappingModal || currentProgramIndex < mappingPrograms.length) {
								handleSkipProgram();
							}

						} else {
							toast.error("No facility details found for this user", {
								className: "custom-toast-error"
							});
						}
					} else {
						toast.error(userResponse.message || "Failed to fetch user details", {
							className: "custom-toast-error"
						});
					}
				}

			} else {
				toast.error(response.data?.data?.response?.message || "Failed to verify OTP", {
					className: "custom-toast-error"
				});
			}
		} catch (error) {
			toast.error("Error in verifying OTP", {
				className: "custom-toast-error"
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleLogin = async (event) => {
		event.preventDefault();
		switch (program) {
			case "NCD":
				await loginNCD();
				break;

			case "SATH":
				await verifySickleOtp();
				break;

			case "Nikshay":
				await loginNikshay();
				break;
			case "U-WIN":
				if (!ShowUwinSendOtp) {
					await loginUwin();
				}
				break;
			case "JANANI":
				await loginJanani();
				break;
			case "RBSK":
				await loginRBSK();
				break;
			case "e-OPD":
				await loginEOPD();
				break;
			default:
				validateAndLoginLocal();
		}
	};

	const handleLogout = async (program) => {

		let programId = null;
		let user_id = null;

		switch (program) {
			case "NCD":
				programId = 1;
				const nUserDetails = localStorage.getItem("NCD_loggedinUserdetails");
				const parsednUser = JSON.parse(nUserDetails || "{}");
				user_id = ncduser?.username || parsednUser?.ncdusername;
				break;
			case "SCD":
				programId = 2;
				const sickleUserDetails = localStorage.getItem("SICKLE_loggedinUserdetails");
				user_id = sickleUserDetails ? JSON.parse(sickleUserDetails)?.username : null;
				break;
			case "Nikshay":
				programId = 4;
				const nikshayUserDetails = localStorage.getItem("NIKSHAY_loggedinUserdetails");
				user_id = nikshayUserDetails ? JSON.parse(nikshayUserDetails)?.username : null;
				break;
			case "U-WIN":
				programId = 6;
				const uwinUserDetails = localStorage.getItem("uwin_loggedinUserdetails");
				user_id = uwinUserDetails ? JSON.parse(uwinUserDetails)?.username : null;
				break;
			case "SATH":
				programId = 7;
				break;
			case "PMNDP":
				programId = 8;
				const pmndpUserDetails = localStorage.getItem("PMNDP_loggedinUserdetails");
				user_id = pmndpUserDetails ? JSON.parse(pmndpUserDetails)?.username : null;
				break;
			case "JANANI":
				programId = 5;
				const jananiUserDetails = localStorage.getItem("JANANI_loggedinUserdetails");
				user_id = jananiUserDetails ? JSON.parse(jananiUserDetails)?.username : null;

				// Call JANANI logout API
				const jananiRefreshToken = localStorage.getItem("JANANI_refreshToken");
				if (jananiRefreshToken) {
					try {
						const response = await rchAPI.rchLogout({ refreshToken: jananiRefreshToken });
						if (response?.success) {
							toast.success(response?.message || "JANANI Logged out successfully");
						} else {
							toast.error(response?.message || "JANANI logout failed");
							return; // Stop logout if API failed
						}
					} catch (error) {
						console.error("JANANI logout error:", error);
						toast.error("JANANI logout failed. Please try again.");
						return; // Stop logout if API threw error
					}
				}
				break;
			case "RBSK":
				programId = 11;
				const rbskUserDetails = localStorage.getItem("RBSK_loggedinUserdetails");
				user_id = rbskUserDetails ? JSON.parse(rbskUserDetails)?.username : null;
				break;
			case "e-OPD":
				programId = 12;
				const eopdUserDetails = localStorage.getItem("e-OPD_loggedinUserdetails");
				user_id = eopdUserDetails ? JSON.parse(eopdUserDetails)?.username : null;

				// Call e-OPD logout API
				const eopdToken = localStorage.getItem("e-OPD_token");
				if (eopdToken) {
					try {
						const response = await eopdAPI.logout();
						if (response?.success) {
							toast.success(response?.message || "e-OPD Logged out successfully");
						} else {
							toast.error(response?.message || "e-OPD logout failed");
							return; // Stop logout if API failed
						}
					} catch (error) {
						console.error("e-OPD logout error:", error);
						toast.error("e-OPD logout failed. Please try again.");
						return; // Stop logout if API threw error
					}
				}
				break;
			default:
				console.error("Unknown program in handleLogout:", program);
				return;
		}
		console.log("handleLogout SCD user_id:", localStorage.getItem("SICKLE_loggedinUserdetails"));
		const success = await unmapProgram(program, programId, user_id);
		if (success) {
			updateLoggedinUserDetails();
			setShowModal(false);
			checkLoggedIn();
		}
	};

	const updateLoggedinUserDetails = () => {
		const programKeyMap = {
			1: "NCD_loggedinUserdetails",
			2: "SICKLE_loggedinUserdetails",
			4: "NIKSHAY_loggedinUserdetails",
			5: "JANANI_loggedinUserdetails",
			6: "uwin_loggedinUserdetails",
			11: "RBSK_loggedinUserdetails",
			12: "e-OPD_loggedinUserdetails",
		};

		let selectedProgramId = null;
		let selectedData = null;

		for (let [id, key] of Object.entries(programKeyMap)) {
			const data = localStorage.getItem(key);

			if (data) {
				selectedProgramId = id;
				selectedData = data;
				break;
			}
		}

		if (selectedData) {
			localStorage.setItem("loggedinUserdetails", selectedData); // optional
			localStorage.setItem("currentLoggedInProgramId", selectedProgramId);
		} else {
			localStorage.removeItem("loggedinUserdetails");
			localStorage.removeItem("currentLoggedInProgramId");
		}
		window.dispatchEvent(new Event("userDetailsUpdated"));
	};

	const getLoggedinClass = (token) => (token ? "one" : "three");

	const handleSkipProgram = () => {
		setShowLoginForm(false);
		const nextIndex = currentProgramIndex + 1;
		if (nextIndex < mappingPrograms.length) {
			setTimeout(() => {
				setCurrentProgramIndex(nextIndex);
			}, 100);
		} else {
			localStorage.setItem('hasSeenProgramMapping', 'true');
			setShowMappingModal(false);
			setCurrentProgramIndex(0);

			// Check if all programs are logged out
			const hasAnyToken =
				localStorage.getItem("NCD_token") ||
				localStorage.getItem("sickle_token") ||
				localStorage.getItem("PMNDP_token") ||
				localStorage.getItem("NIKSHAY_valid_user") ||
				localStorage.getItem("JANANI_token") ||
				localStorage.getItem("RBSK_token") ||
				localStorage.getItem("e-OPD_token") ||
				localStorage.getItem("uwin_token");

			if (!hasAnyToken) {
				toast.info("All programs have been unmapped. Logging out from system.");
				setTimeout(() => {
					checkLoggedIn();
				}, 2000);
			}
		}
	};

	const handleMappingLogin = async () => {
		// Use the same sorting logic as the modal to ensure consistency
		const sortedPrograms = [...mappingPrograms].sort((a, b) => {
			const userMapId = localStorage.getItem("user_map_id");

			const aLoggedIn = !!(a.program_name === "NCD" && localStorage.getItem("NCD_token")) ||
				!!(a.program_name === "SCD" && localStorage.getItem("sickle_token")) ||
				!!(a.program_name === "Nikshay" && localStorage.getItem("NIKSHAY_valid_user")) ||
				!!(a.program_name === "JANANI" && localStorage.getItem("JANANI_token")) ||
				!!(a.program_name === "RBSK" && localStorage.getItem("RBSK_token")) ||
				!!(a.program_name === "e-OPD" && localStorage.getItem("e-OPD_token")) ||
				!!(a.program_name === "UWIN" && localStorage.getItem("uwin_token"));
			const bLoggedIn = !!(b.program_name === "NCD" && localStorage.getItem("NCD_token")) ||
				!!(b.program_name === "SCD" && localStorage.getItem("sickle_token")) ||
				!!(b.program_name === "Nikshay" && localStorage.getItem("NIKSHAY_valid_user")) ||
				!!(b.program_name === "JANANI" && localStorage.getItem("JANANI_token")) ||
				!!(b.program_name === "RBSK" && localStorage.getItem("RBSK_token")) ||
				!!(b.program_name === "e-OPD" && localStorage.getItem("e-OPD_token")) ||
				!!(b.program_name === "UWIN" && localStorage.getItem("uwin_token"));

			// Check if program matches current user_map_id
			const aMatchesUserMapId = (a.program_name === "NCD" && JSON.parse(localStorage.getItem("NCD_loggedinUserdetails") || '{}')?.username === userMapId) ||
				(a.program_name === "SCD" && JSON.parse(localStorage.getItem("SICKLE_loggedinUserdetails") || '{}')?.username === userMapId) ||
				(a.program_name === "Nikshay" && JSON.parse(localStorage.getItem("NIKSHAY_loggedinUserdetails") || '{}')?.username === userMapId) ||
				(a.program_name === "JANANI" && JSON.parse(localStorage.getItem("JANANI_loggedinUserdetails") || '{}')?.username === userMapId) ||
				(a.program_name === "RBSK" && JSON.parse(localStorage.getItem("RBSK_loggedinUserdetails") || '{}')?.username === userMapId) ||
				(a.program_name === "e-OPD" && JSON.parse(localStorage.getItem("e-OPD_loggedinUserdetails") || '{}')?.username === userMapId) ||
				(a.program_name === "UWIN" && JSON.parse(localStorage.getItem("uwin_loggedinUserdetails") || '{}')?.username === userMapId);
			const bMatchesUserMapId = (b.program_name === "NCD" && JSON.parse(localStorage.getItem("NCD_loggedinUserdetails") || '{}')?.username === userMapId) ||
				(b.program_name === "SCD" && JSON.parse(localStorage.getItem("SICKLE_loggedinUserdetails") || '{}')?.username === userMapId) ||
				(b.program_name === "Nikshay" && JSON.parse(localStorage.getItem("NIKSHAY_loggedinUserdetails") || '{}')?.username === userMapId) ||
				(b.program_name === "JANANI" && JSON.parse(localStorage.getItem("JANANI_loggedinUserdetails") || '{}')?.username === userMapId) ||
				(b.program_name === "RBSK" && JSON.parse(localStorage.getItem("RBSK_loggedinUserdetails") || '{}')?.username === userMapId) ||
				(b.program_name === "e-OPD" && JSON.parse(localStorage.getItem("e-OPD_loggedinUserdetails") || '{}')?.username === userMapId) ||
				(b.program_name === "UWIN" && JSON.parse(localStorage.getItem("uwin_loggedinUserdetails") || '{}')?.username === userMapId);

			// Priority: 1. Matches userMapId, 2. Logged in, 3. Not logged in
			if (aMatchesUserMapId !== bMatchesUserMapId) {
				return aMatchesUserMapId ? -1 : 1;
			}

			return Number(bLoggedIn) - Number(aLoggedIn);
		});

		const currentProgram = sortedPrograms[currentProgramIndex];
		let label = '';

		if (currentProgram.program_name === "NCD") {
			label = "NCD";
		} else if (currentProgram.program_name === "SCD") {
			label = "SCD";
		} else if (currentProgram.program_name === "Nikshay") {
			label = "Nikshay";
		} else if (currentProgram.program_name === "UWIN") {
			label = "U-WIN";
		} else if (currentProgram.program_name === "JANANI") {
			label = "JANANI";
		} else if (currentProgram.program_name === "FBNC") {
			label = "FBNC";
		} else if (currentProgram.program_name === "AFHC") {
			label = "AFHC";
		} else if (currentProgram.program_name === "RBSK") {
			label = "RBSK";
		} else if (currentProgram.program_name === "e-OPD") {
			label = "e-OPD";
		}

		setProgram(label);
		setModalText(label);
		setShowLoginForm(true);
	};

	const handleMappingModalClose = () => {
		localStorage.setItem('hasSeenProgramMapping', 'true');
		setShowMappingModal(false);
	};

	return (
		<>
			{isPageLoading && <Loader />}

			<div className="settings-page py-3">

				<div className="container-fluid">
					<section className="program-selection-section" style={{ minHeight: '612px' }}>
						{localStorage.getItem("PMNDP_token") ? (
							<>
								<h4 className="program-selection-title">
									{t('settings.pmndpTitle')}
								</h4>
							</>
						) : (
							<>
								<h4 className="program-selection-title">
									{t('settings.title')}
								</h4>
							</>
						)}

						<div className="row" style={{ minHeight: '490px' }} key={refreshTrigger}>
							{(localStorage.getItem("PMNDP_token")
								? (Array.isArray(activePrograms) ? activePrograms : [])?.filter(
									(prog) => prog.program_name === "PMNDP"
								)
								: (Array.isArray(activePrograms) ? activePrograms : [])?.filter(
									(prog) => prog.program_name !== "PMNDP"
								)
							)?.map((prog, index) => {
								let token;
								let label;

								if (prog.program_name === "NCD") {
									token = ncd_token;
									label = "NCD";
								} else if (prog.program_name === "SCD") {
									token = sickle_token;
									label = "SCD";
								} else if (prog.program_name === "Nikshay") {
									token = nikshay_token;
									label = "Nikshay";
								} else if (prog.program_name === "JANANI") {
									token = janani_token;
									label = "JANANI";
								} else if (prog.program_name === "UWIN") {
									token = uwin_token;
									label = "U-WIN";
								} else if (prog.program_name === "RBSK") {
									token = rbsk_token;
									label = "RBSK";
								} else if (prog.program_name === "e-OPD") {
									token = eopd_token;
									label = "e-OPD";
								} else if (prog.program_name === "PMNDP") {
									token = pmndp_token;
									label = "PMNDP";
								} else if (prog.program_name === "FBNC") {
									token = "";
									label = "FBNC";
								} else if (prog.program_name === "AFHC") {
									token = "";
									label = "AFHC";
								} else {
									token = "";
									label = "";
								}
								const imgSrc = programLogos[prog.program_id];

								return (
									<div className="col-md-3 col-6" key={index}>
										<div className="cardd-wrap">
											<div
												className={`cardd-header ${getLoggedinClass(token)}`}
											>

												<h3 className="program_name">{label}</h3>

												<img src={imgSrc ? imgSrc : ""}
													alt="Program Logo"
													className="icon-setting"
												/>
											</div>
											<div className="cardd-content">

												{prog.program_name === "NCD" && token && (() => {
													const userStr = localStorage.getItem("NCD_loggedinUserdetails");
													if (!userStr) return null;
													let user;
													try {
														user = JSON.parse(userStr);
													} catch (e) {
														console.error("Invalid NCD_loggedinUserdetails JSON", e);
														return null;
													}

													return (
														<>
															<p className="cardd-title">{user?.username}</p>
															<p className="cardd-title">{user?.rolename}</p>
															<p className="cardd-textt">{user?.facilityName}</p>
														</>
													);
												})()}


												{prog.program_name === "SCD" && token && (
													<>
														{
															localStorage.getItem("SICKLE_loggedinUserdetails") && (
																<>
																	<p className="cardd-title">
																		{JSON.parse(
																			localStorage.getItem(
																				"SICKLE_loggedinUserdetails"
																			)
																		)?.username}
																		<>
																			<p className="cardd-title">
																				{JSON.parse(
																					localStorage.getItem(
																						"SICKLE_loggedinUserdetails"
																					)
																				)?.firstname}
																			</p>
																			<p className="cardd-textt">
																				{JSON.parse(
																					localStorage.getItem(
																						"SICKLE_loggedinUserdetails"
																					)
																				)?.rolename}
																			</p>
																		</>
																	</p>
																</>
															)
														}
													</>
												)}

												{prog.program_name === "Nikshay" && token && (() => {
													const userStr = localStorage.getItem("NIKSHAY_loggedinUserdetails");
													if (!userStr) return null;
													let userDetail;
													try {
														userDetail = JSON.parse(userStr);
													} catch (e) {
														console.error("Invalid NIKSHAY_loggedinUserdetails JSON", e);
														return null;
													}

													return (
														<>
															<p className="cardd-title" style={{ minHeight: "70px" }}>{userDetail?.username}</p>
														</>
													);
												})()}

												{prog.program_name === "PMNDP" && (
													<>
														<p className="cardd-title">{user?.firstname}</p>
													</>
												)}

												{prog.program_name === "UWIN" && token && (
													<>
														{
															// get user details from local storage
															localStorage.getItem("uwin_loggedinUserdetails") && (
																<>
																	<p className="cardd-title">
																		{JSON.parse(
																			localStorage.getItem(
																				"uwin_loggedinUserdetails"
																			)
																		)?.firstname}
																	</p>
																	<p className="cardd-textt">
																		{JSON.parse(
																			localStorage.getItem(
																				"uwin_loggedinUserdetails"
																			)
																		)?.facilitydetail}
																	</p>
																</>
															)
														}
													</>
												)}

												{prog.program_name === "JANANI" && token && (() => {
													const userStr = localStorage.getItem("JANANI_loggedinUserdetails");
													if (!userStr) return null;
													let user;
													try {
														user = JSON.parse(userStr);
													} catch (e) {
														console.error("Invalid JANANI_loggedinUserdetails JSON", e);
														return null;
													}

													return (
														<>
															<p className="cardd-title">{user?.username}</p>
															<p className="cardd-title">{user?.rolename}</p>
															<p className="cardd-textt">{user?.facilityName}</p>
														</>
													);
												})()}

												{prog.program_name === "RBSK" && token && (() => {
													const userStr = localStorage.getItem("RBSK_loggedinUserdetails");
													if (!userStr) return null;
													let user;
													try {
														user = JSON.parse(userStr);
													} catch (e) {
														console.error("Invalid RBSK_loggedinUserdetails JSON", e);
														return null;
													}

													return (
														<>
															<p className="cardd-title">{user?.username}</p>
															<p className="cardd-title">{user?.rolename}</p>
															<p className="cardd-textt">{user?.facilityName}</p>
														</>
													);
												})()}

												{prog.program_name === "e-OPD" && token && (() => {
													const userStr = localStorage.getItem("e-OPD_loggedinUserdetails");
													if (!userStr) return null;
													let user;
													try {
														user = JSON.parse(userStr);
													} catch (e) {
														console.error("Invalid e-OPD_loggedinUserdetails JSON", e);
														return null;
													}

													return (
														<>
															<p className="cardd-title">{user?.username}</p>
															<p className="cardd-title">{user?.rolename}</p>
															<p className="cardd-textt">{user?.facilityName}</p>
														</>
													);
												})()}

												<button
													className={`cardd-btn ${getLoggedinClass(token)}`}
													onClick={(e) => handleLabelClick(e, label, imgSrc)}
													// disabled={prog.program_name === "SCD"}
													disabled={prog.program_name === "JANANI" || prog.program_name === "FBNC" || prog.program_name === "AFHC"} // Temporary fix: Disable JANANI, FBNC and AFHC mapping
												>
													{token ? t('settings.logout') : t('settings.login')}
												</button>
											</div>
										</div>
									</div>
								);
							})}
						</div>

						{!localStorage.getItem("PMNDP_token") && (
							<>
								<div className="d-flex mt-2">
									<div className="note-cr mx-2">
										<span>
											<input className="checkboxgreen" />
										</span>
										<span>{t('settings.programSynced')} </span>
									</div>

									<div className="note-cr mx-2">
										<span>
											<input className="checkboxred" />
										</span>
										<span>{t('settings.programNotSynced')}</span>
									</div>
									<div className="note-cr mx-2">
										<span>
											<input className="checkboxgry" />
										</span>
										<span>{t('settings.programNotApplicable')}</span>
									</div>
								</div>
							</>
						)}

						{/* Program Mapping Modal */}

						{showMappingModal && mappingPrograms.length > 0 && (() => {
							const sortedPrograms = [...mappingPrograms].sort((a, b) => {
								const userMapId = localStorage.getItem("user_map_id");
								const aLoggedIn = !!(a.program_name === "NCD" && localStorage.getItem("NCD_token")) ||
									!!(a.program_name === "SCD" && localStorage.getItem("sickle_token")) ||
									!!(a.program_name === "Nikshay" && localStorage.getItem("NIKSHAY_valid_user")) ||
									!!(a.program_name === "JANANI" && localStorage.getItem("JANANI_token")) ||
									!!(a.program_name === "RBSK" && localStorage.getItem("RBSK_token")) ||
									!!(a.program_name === "e-OPD" && localStorage.getItem("e-OPD_token")) ||
									!!(a.program_name === "UWIN" && localStorage.getItem("uwin_token"));
								const bLoggedIn = !!(b.program_name === "NCD" && localStorage.getItem("NCD_token")) ||
									!!(b.program_name === "SCD" && localStorage.getItem("sickle_token")) ||
									!!(b.program_name === "Nikshay" && localStorage.getItem("NIKSHAY_valid_user")) ||
									!!(b.program_name === "JANANI" && localStorage.getItem("JANANI_token")) ||
									!!(b.program_name === "RBSK" && localStorage.getItem("RBSK_token")) ||
									!!(b.program_name === "e-OPD" && localStorage.getItem("e-OPD_token")) ||
									!!(b.program_name === "UWIN" && localStorage.getItem("uwin_token"));

								// Check if program matches current user_map_id
								const aMatchesUserMapId = (a.program_name === "NCD" && JSON.parse(localStorage.getItem("NCD_loggedinUserdetails") || '{}')?.username === userMapId) ||
									(a.program_name === "SCD" && JSON.parse(localStorage.getItem("SICKLE_loggedinUserdetails") || '{}')?.username === userMapId) ||
									(a.program_name === "Nikshay" && JSON.parse(localStorage.getItem("NIKSHAY_loggedinUserdetails") || '{}')?.username === userMapId) ||
									(a.program_name === "JANANI" && JSON.parse(localStorage.getItem("JANANI_loggedinUserdetails") || '{}')?.username === userMapId) ||
									(a.program_name === "RBSK" && JSON.parse(localStorage.getItem("RBSK_loggedinUserdetails") || '{}')?.username === userMapId) ||
									(a.program_name === "e-OPD" && JSON.parse(localStorage.getItem("e-OPD_loggedinUserdetails") || '{}')?.username === userMapId) ||
									(a.program_name === "UWIN" && JSON.parse(localStorage.getItem("uwin_loggedinUserdetails") || '{}')?.username === userMapId) ||
									(a.program_name === "UWIN" && JSON.parse(localStorage.getItem("uwin_loggedinUserdetails") || '{}')?.username === userMapId);
								const bMatchesUserMapId = (b.program_name === "NCD" && JSON.parse(localStorage.getItem("NCD_loggedinUserdetails") || '{}')?.username === userMapId) ||
									(b.program_name === "SCD" && JSON.parse(localStorage.getItem("SICKLE_loggedinUserdetails") || '{}')?.username === userMapId) ||
									(b.program_name === "Nikshay" && JSON.parse(localStorage.getItem("NIKSHAY_loggedinUserdetails") || '{}')?.username === userMapId) ||
									(b.program_name === "JANANI" && JSON.parse(localStorage.getItem("JANANI_loggedinUserdetails") || '{}')?.username === userMapId) ||
									(b.program_name === "RBSK" && JSON.parse(localStorage.getItem("RBSK_loggedinUserdetails") || '{}')?.username === userMapId) ||
									(b.program_name === "e-OPD" && JSON.parse(localStorage.getItem("e-OPD_loggedinUserdetails") || '{}')?.username === userMapId) ||
									(b.program_name === "UWIN" && JSON.parse(localStorage.getItem("uwin_loggedinUserdetails") || '{}')?.username === userMapId);

								// Priority: 1. Matches userMapId, 2. Logged in, 3. Not logged in
								if (aMatchesUserMapId !== bMatchesUserMapId) {
									return aMatchesUserMapId ? -1 : 1;
								}

								return Number(bLoggedIn) - Number(aLoggedIn);
							});

							const currentProgram = sortedPrograms[currentProgramIndex];

							return (
								<div
									className="modal fade show d-block verificationModel"
									id="mappingModal"
									tabIndex={-1}
									style={{ background: "rgba(0,0,0,0.5)" }}
								>
									<div className="modal-dialog modal-lg">
										<div className="modal-content">
											<div className="modal-header border-bottom-0">
												<h5 className="modal-title">Program Setup - Step {currentProgramIndex + 1} of {sortedPrograms.length}</h5>
												<button
													type="button"
													className="btn-close"
													onClick={handleMappingModalClose}
												/>
											</div>
											<div className="modal-body text-center">
												<div className="mb-4">
													<div
														className="program-logo-container new-style_setting"
														style={{
															background: (() => {
																const isLoggedIn =
																	(currentProgram?.program_name === "NCD" && localStorage.getItem("NCD_token")) ||
																	(currentProgram?.program_name === "SCD" && localStorage.getItem("sickle_token")) ||
																	(currentProgram?.program_name === "Nikshay" && localStorage.getItem("NIKSHAY_valid_user")) ||
																	(currentProgram?.program_name === "JANANI" && localStorage.getItem("JANANI_token")) ||
																	(currentProgram?.program_name === "RBSK" && localStorage.getItem("RBSK_token")) ||
																	(currentProgram?.program_name === "e-OPD" && localStorage.getItem("e-OPD_token")) ||
																	(currentProgram?.program_name === "UWIN" && localStorage.getItem("uwin_token"));

																return isLoggedIn
																	? 'linear-gradient(to left,  rgba(17, 153, 142, 0.1), rgba(56, 239, 125, 0.1))' // Green gradient for logged-in
																	: 'linear-gradient(to left, rgba(190, 0, 0, 0.1), rgba(238, 113, 113, 0.1))'; // Red gradient for not logged-in
															})(),
														}}
													>
														<img
															src={
																currentProgram?.program_id === 1
																	? "/images/NCDskip.png"
																	: currentProgram?.program_id === 2
																		? "/images/Sickleskip.png"
																		: currentProgram?.program_id === 4
																			? "/images/nikshayskip.png"
																			: currentProgram?.program_id === 6
																				? "/images/uwinskip.png"
																				: currentProgram?.program_id === 8
																					? "/images/PNDPskip.png"
																					: currentProgram?.program_id === 5
																						? "/images/jananiskip.png"
																						: currentProgram?.program_id === 9
																							? "/images/programs_logo/fbnc.png"
																							: currentProgram?.program_id === 10
																								? "/images/programs_logo/afhc.png"
																								: currentProgram?.program_id === 11
																									? "/images/rbsk-skip.png"
																									: currentProgram?.program_id === 12
																										? "/images/e-opd-skip.png"
																										: "/images/NCDskip.png"
															}
															alt="Program Logo"
															className="abhaLogo"
															style={{ backgroundColor: 'transparent' }}
														/>
													</div>
													{(() => {
														const isLoggedIn =
															(currentProgram?.program_name === "NCD" && localStorage.getItem("NCD_token")) ||
															(currentProgram?.program_name === "SCD" && localStorage.getItem("sickle_token")) ||
															(currentProgram?.program_name === "Nikshay" && localStorage.getItem("NIKSHAY_valid_user")) ||
															(currentProgram?.program_name === "JANANI" && localStorage.getItem("JANANI_token")) ||
															(currentProgram?.program_name === "RBSK" && localStorage.getItem("RBSK_token")) ||
															(currentProgram?.program_name === "e-OPD" && localStorage.getItem("e-OPD_token")) ||
															(currentProgram?.program_name === "UWIN" && localStorage.getItem("uwin_token"));

														return (
															<>
																<h4 className="mt-3">
																	{isLoggedIn
																		? `Manage your ${currentProgram?.program_name} account`
																		: `Connect your ${currentProgram?.program_name} account`
																	}
																</h4>
																<p className="text-muted">
																	{isLoggedIn
																		? <>You are currently logged into <strong>{currentProgram?.program_name}</strong>. You can logout if needed.</>
																		: <>Link your existing <strong>{currentProgram?.program_name}</strong> account to access all features in one place.</>
																	}
																</p>
															</>
														);
													})()}
												</div>

												{/* Login Form - appears inline when showLoginForm is true */}
												{showLoginForm && (
													<div className="login-form-inline mb-4">
														<Form onSubmit={handleLogin}>
															{program === "U-WIN" ? (
																<>
																	{!ShowUwinSendOtp ? (
																		<>
																			<div className="input-container mb-3">
																				<input
																					type="text"
																					placeholder="Enter Username/Email bbbbbbbb"
																					value={email}
																					onChange={(e) => setEmail(e.target.value)}
																					className="form-control"
																				/>
																			</div>
																			<div className="input-container mb-3">
																				<input
																					type="password"
																					placeholder="Enter your password"
																					value={password}
																					onChange={(e) => setPassword(e.target.value)}
																					className="form-control"
																				/>
																			</div>
																		</>
																	) : (
																		<div className="d-flex justify-content-center gap-2 mb-3">
																			{Array.isArray(uwinOtp) &&
																				uwinOtp.map((value, index) => (
																					<input
																						key={index}
																						id={`uwin-otp-input-${index}`}
																						type="text"
																						maxLength="1"
																						value={value}
																						onChange={(e) => {
																							if (!/^\d*$/.test(e.target.value)) return;
																							const newOtp = [...uwinOtp];
																							newOtp[index] = e.target.value;
																							setUwinOtp(newOtp);
																							setUwinOtpText(newOtp.join(""));
																							if (e.target.value && index < 5) {
																								document.getElementById(`uwin-otp-input-${index + 1}`)?.focus();
																							}
																						}}
																						onKeyDown={(e) => {
																							if (e.key === "Backspace" && !uwinOtp[index] && index > 0) {
																								document.getElementById(`uwin-otp-input-${index - 1}`)?.focus();
																							}
																						}}
																						className="form-control text-center"
																						style={{ width: "40px", height: "40px" }}
																					/>
																				))}
																		</div>
																	)}
																</>
															) : program !== "SCD" ? (
																<>
																	<div className="input-container mb-3">
																		<input
																			type="text"
																			placeholder="Enter Username/Email jskjaskjakdjakdkasajdk"
																			value={email}
																			onChange={(e) => setEmail(e.target.value)}
																			className="form-control"
																		/>
																	</div>
																	<div className="input-container mb-3">
																		<input
																			type="password"
																			placeholder="Enter your password"
																			value={password}
																			onChange={(e) => setPassword(e.target.value)}
																			className="form-control"
																		/>
																	</div>
																</>
															) : (
																<>
																	<div className="input-container mb-3">
																		<input
																			type="text"
																			maxLength="10"
																			value={mobileNumber}
																			onChange={handleMobileValidation}
																			placeholder="Enter Mobile Number"
																			className="form-control"
																		/>
																		{ShowSendOtp && (
																			<small>
																				<a
																					style={{
																						pointerEvents: resendTimer > 0 ? "none" : "auto",
																						color: resendTimer > 0 ? "#999" : "#007bff",
																					}}
																					href="javascript:void(0)"
																					onClick={handleSickleAuth}
																				>
																					{resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : "Resend OTP ?"}
																				</a>
																			</small>
																		)}
																	</div>
																	{ShowSendOtp && (
																		<div className="d-flex justify-content-center gap-2 mb-3">
																			{Array.isArray(sickleOtp) &&
																				sickleOtp.map((value, index) => (
																					<input
																						key={index}
																						id={`otp-input-${index}`}
																						type="text"
																						maxLength="1"
																						value={value}
																						onChange={(e) => handleSickleOtpChange(e.target.value, index)}
																						onKeyDown={(e) => handleBackspace(e, index)}
																						className="form-control text-center"
																						style={{ width: "40px", height: "40px" }}
																					/>
																				))}
																		</div>
																	)}
																</>
															)}
														</Form>
													</div>
												)}

												{/* Buttons */}
												<div className="d-flex justify-content-center gap-3">
													{showLoginForm ? (
														<>
															<button
																className="btn btn-secondary px-4"
																onClick={() => setShowLoginForm(false)}
															>
																Back
															</button>
															<button
																className="btn btn-primary px-4"
																onClick={(e) => {
																	e.preventDefault();

																	// Handle UWIN OTP verification
																	if (program === "U-WIN" && ShowUwinSendOtp) {
																		if (!UwinOtpText.trim() || UwinOtpText.length !== 6) {
																			toast.error("Please enter complete 6-digit OTP");
																			return;
																		}
																		handleUwinVerifyOtp();
																		return;
																	}

																	// Validation for other programs
																	if (program !== "SCD") {
																		if (!email.trim()) {
																			toast.error("Please enter username/email");
																			return;
																		}
																		if (!password.trim()) {
																			toast.error("Please enter password");
																			return;
																		}
																	} else {
																		if (!ShowSendOtp) {
																			if (!mobileNumber.trim() || mobileNumber.length !== 10) {
																				toast.error("Please enter valid 10-digit mobile number");
																				return;
																			}
																		} else {
																			if (!SickleOtpText.trim() || SickleOtpText.length !== 6) {
																				toast.error("Please enter complete 6-digit OTP");
																				return;
																			}
																		}
																	}

																	handleLogin(e);
																}}
																disabled={isLoading}
															>
																{program === "U-WIN" && ShowUwinSendOtp
																	? (isLoading ? "Verifying..." : "Verify OTP")
																	: (isLoading ? "Syncing..." : "Sync")
																}
															</button>
															<button
																className="btn btn-outline-secondary px-4"
																onClick={handleSkipProgram}
															>
																Skip
															</button>
														</>
													) : (
														<>
															{(() => {
																const isLoggedIn =
																	(currentProgram?.program_name === "NCD" && localStorage.getItem("NCD_token")) ||
																	(currentProgram?.program_name === "SCD" && localStorage.getItem("sickle_token")) ||
																	(currentProgram?.program_name === "Nikshay" && localStorage.getItem("NIKSHAY_valid_user")) ||
																	(currentProgram?.program_name === "JANANI" && localStorage.getItem("JANANI_token")) ||
																	(currentProgram?.program_name === "RBSK" && localStorage.getItem("RBSK_token")) ||
																	(currentProgram?.program_name === "e-OPD" && localStorage.getItem("e-OPD_token")) ||
																	(currentProgram?.program_name === "UWIN" && localStorage.getItem("uwin_token"));

																return isLoggedIn ? (
																	<button
																		className="btn btn-danger px-4"
																		onClick={() => {
																			setLogoutProgramName(currentProgram?.program_name);
																			setShowLogoutConfirm(true);
																		}}
																	>
																		Logout
																	</button>
																) : (
																	<button
																		className="btn btn-primary px-4"
																		onClick={handleMappingLogin}
																		disabled={currentProgram?.program_name === "SCD" || currentProgram?.program_name === "JANANI" || currentProgram?.program_name === "FBNC" || currentProgram?.program_name === "AFHC"}
																	>
																		Map Account
																	</button>
																);
															})()}
															<button
																className="btn btn-outline-secondary px-4"
																onClick={handleSkipProgram}
															>
																Skip for Now
															</button>
														</>
													)}
												</div>
											</div>
										</div>
									</div>
								</div>
							);
						})()}

						{/* Replace Mapping Modal */}
						{showReplaceModal && replaceMappingData && (
							<div
								className="modal fade show d-block"
								style={{ background: "rgba(0,0,0,0.5)", zIndex: 1200 }}
							>
								<div className="modal-dialog">
									<div className="modal-content">
										<div className="modal-header">
											<h5 className="modal-title">Account Already Mapped</h5>
											<button
												type="button"
												className="btn-close"
												onClick={() => {
													setShowReplaceModal(false);
													setReplaceMappingData(null);
												}}
											/>
										</div>
										<div className="modal-body text-center">
											<p>
												You have already mapped a different account for <strong>{replaceMappingData.program}</strong>.
											</p>
											<p className="text-muted">
												Current mapped account: <strong>{replaceMappingData.existingUser}</strong>
											</p>
											<p>
												Do you want to replace the existing mapping with this account?
											</p>
										</div>
										<div className="modal-footer">
											<button
												type="button"
												className="btn btn-secondary"
												onClick={() => {
													setShowReplaceModal(false);
													setReplaceMappingData(null);
												}}
											>
												Cancel
											</button>
											<button
												type="button"
												className="btn btn-primary"
												onClick={async () => {
													const success = await replaceMapping(
														replaceMappingData.program,
														replaceMappingData.credentials
													);
													if (success) {
														setShowReplaceModal(false);
														setReplaceMappingData(null);
														setShowModal(false);

														// Continue mapping flow if in progress
														if (showMappingModal || currentProgramIndex < mappingPrograms.length) {
															handleSkipProgram();
														}
													}
												}}
												disabled={isLoading}
											>
												{isLoading ? "Updating..." : "Replace Mapping"}
											</button>
										</div>
									</div>
								</div>
							</div>
						)}

						{/* Logout Confirmation Modal */}
						{showLogoutConfirm && (
							<div
								className="modal fade show d-block"
								style={{ background: "rgba(0,0,0,0.5)", zIndex: 1100 }}
							>
								<div className="modal-dialog modal-sm">
									<div className="modal-content">
										<div className="modal-header">
											<h5 className="modal-title">Confirm Logout</h5>
											<button
												type="button"
												className="btn-close"
												onClick={() => setShowLogoutConfirm(false)}
											/>
										</div>
										<div className="modal-body text-center">
											<p>Are you sure you want to logout from <strong>{logoutProgramName}</strong>?</p>
										</div>
										<div className="modal-footer">
											<button
												type="button"
												className="btn btn-secondary"
												onClick={() => setShowLogoutConfirm(false)}
											>
												Cancel
											</button>
											<button
												type="button"
												className="btn btn-danger"
												onClick={async () => {
													const success = await unmapProgram(logoutProgramName);
													if (success) {
														setShowLogoutConfirm(false);
														handleSkipProgram();
													}
												}}
												disabled={isLoading}
											>
												{isLoading ? "Logging out..." : "Logout"}
											</button>
										</div>
									</div>
								</div>
							</div>
						)}

						{showModal && (
							<div
								className="modal fade show d-block verificationModel"
								id="exampleModal"
								tabIndex={-1}
								aria-labelledby="exampleModalLabel"
								aria-hidden="true"
								style={{ background: "rgba(0,0,0,0.5)" }}
							>
								<div className="modal-dialog modal-lg">
									<div className="modal-content">
										<div className="modal-header border-bottom-0 ">
											<button
												type="button"
												className="btn-close"
												data-bs-dismiss="modal"
												aria-label="Close"
												onClick={closeModal}
											/>
										</div>
										<div className="modal-body">
											<div className="row Abharow">
												{modalText.includes(
													"Are you sure you want to log out"
												) && (
														<div className="col-md-12 text-center">
															<img src={imgSrc} alt="" className="abhaLogo" />
															<h4>{modalText}</h4>
															<button
																className="btn btn-lg btn-danger"
																onClick={closeModal}
																style={{ margin: "20px 50px 0 0" }}
															>
																No
															</button>

															<button
																className="btn btn-lg btn-success logout"
																onClick={() => handleLogout(program)}
																style={{ margin: "20px 0 0 50px" }}
															>
																Yes
															</button>
														</div>
													)}
												{!modalText.includes(
													"Are you sure you want to log out"
												) && (
														<div className="col-md-6 text-center">
															<img src={imgSrc} alt="" className="abhaLogo" />
															<h2>{modalText}</h2>
														</div>
													)}
												{!modalText.includes(
													"Are you sure you want to log out"
												) && (
														<div className="col-md-6">
															<Form
																className="w-100 Login-res"
																onSubmit={handleLogin}
															>
																{program === "U-WIN" && ShowUwinSendOtp ? (
																	<>
																		<div className="input-container">
																			<label className="header_text">
																				Verify OTP for {modalText}
																			</label>
																			<div
																				style={{
																					display: "flex",
																					gap: "10px",
																					marginTop: "25px",
																					justifyContent: "center"
																				}}
																			>
																				{uwinOtp.map((value, index) => (
																					<input
																						key={index}
																						id={`uwin-otp-input-${index}`}
																						type="text"
																						maxLength="1"
																						value={value}
																						onChange={(e) => {
																							if (!/^\d*$/.test(e.target.value)) return;
																							const newOtp = [...uwinOtp];
																							newOtp[index] = e.target.value;
																							setUwinOtp(newOtp);
																							setUwinOtpText(newOtp.join(""));
																							if (e.target.value && index < 5) {
																								document.getElementById(`uwin-otp-input-${index + 1}`)?.focus();
																							}
																						}}
																						onKeyDown={(e) => {
																							if (e.key === "Backspace" && !uwinOtp[index] && index > 0) {
																								document.getElementById(`uwin-otp-input-${index - 1}`)?.focus();
																							}
																						}}
																						className="form-control"
																						style={{
																							width: "40px",
																							height: "40px",
																							textAlign: "center",
																							fontSize: "18px",
																						}}
																					/>
																				))}
																			</div>
																		</div>
																	</>
																) : program !== "SCD" ? (
																	<>
																		<div className="input-container">
																			<label className="header_text">
																				Sync {modalText} Account
																			</label>

																			<input
																				type="text"
																				id="email"
																				placeholder="Enter Username/Email"
																				value={email}
																				onChange={(e) => setEmail(e.target.value)}
																				className="modal-input form-control form-text-control"
																			/>
																		</div>
																		<div className="input-container">
																			<input
																				type="password"
																				id="password"
																				placeholder="Enter your password"
																				value={password}
																				onChange={(e) =>
																					setPassword(e.target.value)
																				}
																				className="modal-input form-control form-text-control"
																			/>
																		</div>
																	</>
																) : (
																	<>
																		<div className="input-container">
																			<label className="header_text">
																				Sync {modalText} Account
																			</label>

																			<input
																				type="text"
																				id="mobile"
																				maxLength="10"
																				value={mobileNumber}
																				onChange={handleMobileValidation}
																				autoComplete="off"
																				placeholder="Enter Mobile Number"
																				className="modal-input form-control form-text-control"
																			/>
																			{ShowSendOtp && (
																				<a
																					className="pull-right"
																					style={{
																						pointerEvents:
																							resendTimer > 0 ? "none" : "auto",
																						color:
																							resendTimer > 0
																								? "#999"
																								: "#007bff",
																						cursor:
																							resendTimer > 0
																								? "not-allowed"
																								: "pointer",
																					}}
																					href="javascript:void(0)"
																					onClick={handleSickleAuth}
																				>
																					{resendTimer > 0
																						? `Resend OTP in ${resendTimer}s`
																						: "Resend OTP ?"}
																				</a>
																			)}
																		</div>
																		{ShowSendOtp && (
																			<div
																				style={{
																					display: "flex",
																					gap: "10px",
																					marginTop: "25px",
																				}}
																			>
																				{Array.isArray(sickleOtp) &&
																					sickleOtp.map((value, index) => (
																						<input
																							key={index}
																							id={`otp-input-${index}`}
																							type="text"
																							maxLength="1"
																							name="sickleOtp"
																							value={value}
																							onChange={(e) =>
																								handleSickleOtpChange(
																									e.target.value,
																									index
																								)
																							}
																							onKeyDown={(e) =>
																								handleBackspace(e, index)
																							}
																							className="form-control"
																							autoComplete="off"
																							style={{
																								width: "40px",
																								height: "40px",
																								textAlign: "center",
																								fontSize: "18px",
																							}}
																						/>
																					))}
																			</div>
																		)}
																	</>
																)}

																<div className="modal-buttons">
																	{program === "U-WIN" && ShowUwinSendOtp ? (
																		<button
																			className="modal-button modal-button-secondary pull-right"
																			type="button"
																			onClick={handleUwinVerifyOtp}
																			disabled={isLoading}
																		>
																			{isLoading ? "Verifying..." : "Verify OTP"}
																		</button>
																	) : program === "U-WIN" && !ShowUwinSendOtp ? (
																		<button
																			className="modal-button modal-button-secondary pull-right"
																			type="submit"
																			disabled={isLoading}
																		>
																			{isLoading ? "Syncing..." : "Sync"}
																		</button>
																	) : program === "SCD" && ShowSendOtp ? (
																		<button
																			className="modal-button modal-button-secondary pull-right"
																			type="submit"
																			disabled={isLoading}
																		>
																			{isLoading ? "syncing..." : "Sync"}
																		</button>
																	) : program !== "SCD" && program !== "U-WIN" ? (
																		<button
																			className="modal-button modal-button-secondary pull-right"
																			type="submit"
																		>
																			{isLoading ? "syncing..." : "Sync"}
																		</button>
																	) : null}
																</div>
															</Form>
															<div className="modal-buttons">
																{!ShowSendOtp && program === "SCD" && (
																	<button
																		className="modal-button modal-button-secondary pull-right"
																		disabled={isLoading}
																		onClick={handleSickleAuth}
																	>
																		{" "}
																		{isLoading ? "Logging in..." : "Get OTP"}{" "}
																	</button>
																)}
															</div>
														</div>
													)}
											</div>
										</div>
									</div>
								</div>
							</div>
						)}
					</section>
				</div>
			</div>

			<Footer />
		</>
	);
};